import { useState, useMemo } from "react";
import { 
  HelpCircle, 
  Users, 
  Briefcase, 
  Shield,
  ChevronDown,
  ChevronRight,
  FileText,
  Download,
  Search,
  Calendar,
  CreditCard,
  MessageSquare,
  LogIn,
  Truck,
  ClipboardList,
  Building2,
  Lock,
  PartyPopper,
  ShoppingBag,
  BarChart3,
  Sparkles,
  BookOpen,
  Play,
  CheckCircle2,
  ArrowRight,
  Image as ImageIcon
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images des guides
const guideImages = {
  connexion: "https://static.prod-images.emergentagent.com/jobs/a66e27c2-5f47-475e-af5b-7557aec4df08/images/67df891f1db065f40f29355a1093ff9325143d10ef648b2306fc3f3428bc0830.png",
  planning: "https://static.prod-images.emergentagent.com/jobs/a66e27c2-5f47-475e-af5b-7557aec4df08/images/27b16fb2bb6af1f3f20246b48c888f540702b9d6c381cd140f34e6d28ccdfb43.png",
  organisation: "https://static.prod-images.emergentagent.com/jobs/a66e27c2-5f47-475e-af5b-7557aec4df08/images/e70269299be01034e4821176fbe4e6431f56fac88618ee1ec3df91ce96c8f903.png",
  services: "https://static.prod-images.emergentagent.com/jobs/a66e27c2-5f47-475e-af5b-7557aec4df08/images/192d0a005c222dc086b729fa77b60c14a816721928cceae8fa38a280b3b3ccc4.png"
};

// Nouveautés récentes
const recentUpdates = [
  {
    date: "Mars 2026",
    title: "Organisation Événements",
    description: "Nouveau workflow de sondage pour les événements organisés par le bureau avec assignation automatique des food trucks.",
    isNew: true
  },
  {
    date: "Mars 2026",
    title: "Achats Bureau améliorés",
    description: "Paiement en ligne via Stripe, répartition automatique et notifications email.",
    isNew: true
  }
];

// Guides détaillés avec étapes
const detailedGuides = {
  membre: [
    {
      id: "guide-connexion",
      title: "Se connecter à l'application",
      icon: LogIn,
      image: guideImages.connexion,
      description: "Accédez à votre espace membre en quelques clics",
      steps: [
        { step: 1, title: "Accéder à la page de connexion", description: "Rendez-vous sur l'URL de l'application. Vous arrivez automatiquement sur la page de connexion." },
        { step: 2, title: "Saisir vos identifiants", description: "Entrez votre adresse email et votre mot de passe dans les champs correspondants." },
        { step: 3, title: "Cliquer sur 'Se connecter'", description: "Appuyez sur le bouton rouge pour accéder à votre tableau de bord." },
        { step: 4, title: "Mot de passe oublié ?", description: "Cliquez sur le lien 'Mot de passe oublié' pour recevoir un email de réinitialisation." }
      ]
    },
    {
      id: "guide-planning-membre",
      title: "Consulter le planning",
      icon: Calendar,
      image: guideImages.planning,
      description: "Visualisez vos événements et répondez aux invitations",
      steps: [
        { step: 1, title: "Accéder au Planning", description: "Cliquez sur 'Planning' dans le menu de gauche pour voir le calendrier." },
        { step: 2, title: "Voir vos événements", description: "Les événements auxquels vous participez sont affichés dans le calendrier avec des couleurs distinctes." },
        { step: 3, title: "Répondre aux sondages", description: "Si vous avez des invitations, cliquez sur 'Intéressé' ou 'Pas intéressé' pour répondre." },
        { step: 4, title: "Proposer un événement", description: "Cliquez sur le bouton rouge 'Proposer un événement' pour soumettre une nouvelle proposition." }
      ]
    },
    {
      id: "guide-mes-services",
      title: "Gérer mes services",
      icon: ClipboardList,
      image: guideImages.services,
      description: "Suivez vos participations et paiements",
      steps: [
        { step: 1, title: "Accéder à Mes Services", description: "Cliquez sur 'Mes Services' dans le menu pour voir tous vos événements assignés." },
        { step: 2, title: "Voir le statut de paiement", description: "Chaque service affiche son statut : En attente, Payé ou Exempté." },
        { step: 3, title: "Consulter les détails", description: "Cliquez sur un événement pour voir le lieu, la date et les informations complémentaires." },
        { step: 4, title: "Remplir le bilan", description: "Après l'événement, remplissez votre bilan de prestation avec le CA réalisé." }
      ]
    },
    {
      id: "guide-paiements",
      title: "Effectuer un paiement",
      icon: CreditCard,
      description: "Réglez vos cotisations et frais en ligne",
      steps: [
        { step: 1, title: "Accéder à Mes Paiements", description: "Cliquez sur 'Mes Paiements' pour voir votre solde et les factures." },
        { step: 2, title: "Choisir le paiement", description: "Sélectionnez la cotisation ou les frais à régler." },
        { step: 3, title: "Payer en ligne", description: "Cliquez sur 'Payer en ligne' pour accéder au paiement sécurisé par carte." },
        { step: 4, title: "Confirmation", description: "Vous recevez un email de confirmation et la facture est disponible dans votre espace." }
      ]
    }
  ],
  bureau: [
    {
      id: "guide-organisation-events",
      title: "Organiser un événement",
      icon: PartyPopper,
      image: guideImages.organisation,
      isNew: true,
      description: "Créez et gérez les événements de l'association",
      steps: [
        { step: 1, title: "Créer l'événement", description: "Dans 'Organisation Events', cliquez sur 'Nouvel Événement' et remplissez les informations : titre, client, lieu, dates, nombre de food trucks requis." },
        { step: 2, title: "Envoyer le sondage", description: "Une fois créé, cliquez sur l'icône d'envoi pour notifier tous les food trucks correspondant aux spécialités recherchées." },
        { step: 3, title: "Consulter les réponses", description: "Les food trucks intéressés apparaissent dans la liste. Le compteur 'X intéressés' se met à jour." },
        { step: 4, title: "Assigner les food trucks", description: "Cliquez sur l'icône 'utilisateurs', sélectionnez les food trucks, assignez-les aux services (midi/soir) et validez." },
        { step: 5, title: "Notifications automatiques", description: "Les food trucks sélectionnés reçoivent un email de confirmation avec les détails de la prestation." }
      ]
    },
    {
      id: "guide-planning-global",
      title: "Planning Global",
      icon: BarChart3,
      description: "Vue d'ensemble de tous les événements",
      steps: [
        { step: 1, title: "Accéder au Planning Global", description: "Cliquez sur 'Planning Global' dans le menu pour voir tous les événements validés." },
        { step: 2, title: "Filtrer par food truck", description: "Utilisez le menu déroulant pour voir les événements d'un food truck spécifique." },
        { step: 3, title: "Détecter les conflits", description: "Le système signale automatiquement si un food truck est assigné à plusieurs événements le même jour." },
        { step: 4, title: "Voir les participants", description: "Chaque événement affiche la liste des food trucks participants avec leur statut de paiement." }
      ]
    },
    {
      id: "guide-achats-bureau",
      title: "Gérer les achats bureau",
      icon: ShoppingBag,
      isNew: true,
      description: "Créez des achats et répartissez les frais",
      steps: [
        { step: 1, title: "Créer un achat", description: "Dans 'Achats Bureau', cliquez sur 'Nouvel achat' et entrez la description et le montant total." },
        { step: 2, title: "Sélectionner les food trucks", description: "Cochez les food trucks qui doivent participer aux frais." },
        { step: 3, title: "Répartition automatique", description: "Le montant est divisé équitablement. Les membres bureau sont automatiquement exemptés." },
        { step: 4, title: "Notifier les membres", description: "Les food trucks concernés reçoivent un email avec leur quote-part et un lien de paiement." },
        { step: 5, title: "Suivre les paiements", description: "Consultez le statut de chaque paiement. Marquez les paiements en espèces manuellement." }
      ]
    },
    {
      id: "guide-crm",
      title: "Utiliser le CRM",
      icon: Building2,
      description: "Gérez vos prospects et clients",
      steps: [
        { step: 1, title: "Voir le pipeline", description: "Le CRM affiche les prospects en colonnes : Prospect → Qualifié → Proposition → Client." },
        { step: 2, title: "Ajouter un prospect", description: "Cliquez sur '+' pour créer une nouvelle fiche avec les informations du contact." },
        { step: 3, title: "Déplacer dans le pipeline", description: "Glissez-déposez les cartes pour faire avancer les prospects dans le processus." },
        { step: 4, title: "Générer un devis", description: "Ouvrez la fiche client et cliquez sur 'Générer un devis' pour créer une proposition commerciale." }
      ]
    }
  ],
  admin: [
    {
      id: "guide-utilisateurs",
      title: "Gérer les utilisateurs",
      icon: Users,
      description: "Créez et administrez les comptes",
      steps: [
        { step: 1, title: "Accéder aux Utilisateurs", description: "Cliquez sur 'Utilisateurs' pour voir la liste de tous les comptes." },
        { step: 2, title: "Créer un utilisateur", description: "Cliquez sur 'Nouvel utilisateur', entrez l'email, choisissez le rôle (Admin, Bureau, Membre)." },
        { step: 3, title: "Modifier un rôle", description: "Ouvrez la fiche utilisateur et changez le rôle dans le menu déroulant." },
        { step: 4, title: "Associer un food truck", description: "Pour les membres, associez leur food truck dans le champ dédié." }
      ]
    },
    {
      id: "guide-permissions",
      title: "Configurer les droits",
      icon: Shield,
      description: "Personnalisez les accès par module",
      steps: [
        { step: 1, title: "Accéder à Gestion des Droits", description: "Cliquez sur 'Gestion des Droits' pour voir les permissions par rôle." },
        { step: 2, title: "Sélectionner un rôle", description: "Choisissez l'onglet Admin, Bureau ou Membre pour voir ses permissions." },
        { step: 3, title: "Modifier les niveaux", description: "Pour chaque module, choisissez : Aucun, Lecture, Écriture ou Admin." },
        { step: 4, title: "Créer des exceptions", description: "Dans l'onglet Exceptions, ajoutez des permissions spécifiques pour certains utilisateurs." }
      ]
    },
    {
      id: "guide-validations",
      title: "Valider les inscriptions",
      icon: CheckCircle2,
      description: "Approuvez les nouveaux food trucks",
      steps: [
        { step: 1, title: "Accéder aux Validations", description: "Cliquez sur 'Validations' pour voir les inscriptions en attente." },
        { step: 2, title: "Vérifier les documents", description: "Consultez chaque document fourni : KBIS, assurances, certifications." },
        { step: 3, title: "Valider ou refuser", description: "Cliquez sur 'Valider' pour approuver ou 'Refuser' avec un motif." },
        { step: 4, title: "Notification automatique", description: "Le food truck reçoit un email l'informant de la décision." }
      ]
    }
  ]
};

// FAQ rapide
const quickFAQ = {
  membre: [
    { q: "Je ne vois pas mes événements", a: "Seuls les événements auxquels vous participez sont affichés. Si vous n'avez pas encore été sélectionné, l'événement n'apparaît pas." },
    { q: "Comment savoir si je suis sélectionné ?", a: "Vous recevez un email de confirmation. L'événement apparaît ensuite dans 'Mes Services'." },
    { q: "Pourquoi je suis marqué 'Exempté' ?", a: "Les membres du bureau sont automatiquement exemptés des frais de participation de 20€." },
    { q: "Mon compte est bloqué", a: "Votre cotisation n'est pas à jour. Accédez à 'Mes Paiements' pour régulariser." }
  ],
  bureau: [
    { q: "Comment envoyer un sondage ?", a: "Dans Organisation Events, cliquez sur l'icône d'envoi à côté de l'événement." },
    { q: "Les food trucks ne reçoivent pas l'email", a: "Vérifiez que leurs spécialités correspondent à celles recherchées pour l'événement." },
    { q: "Comment voir qui a répondu ?", a: "Le compteur 'X intéressés' indique le nombre de réponses positives." },
    { q: "Un food truck veut annuler", a: "Dans la gestion des participants, vous pouvez retirer un food truck assigné." }
  ],
  admin: [
    { q: "Comment débloquer un compte ?", a: "Marquez la cotisation comme payée dans la fiche de l'adhérent." },
    { q: "Un utilisateur n'a pas accès à un module", a: "Vérifiez ses permissions dans 'Gestion des Droits' ou créez une exception." },
    { q: "Comment réinitialiser un mot de passe ?", a: "L'utilisateur doit utiliser 'Mot de passe oublié' sur la page de connexion." }
  ]
};

function GuideCard({ guide, onSelect }) {
  const Icon = guide.icon;
  
  return (
    <Card 
      className="cursor-pointer hover:border-[#7A2D2D] hover:shadow-md transition-all"
      onClick={() => onSelect(guide)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#7A2D2D]/10 shrink-0">
            <Icon className="w-5 h-5 text-[#7A2D2D]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading font-semibold text-secondary truncate">{guide.title}</h3>
              {guide.isNew && (
                <Badge className="bg-green-100 text-green-700 text-xs shrink-0">Nouveau</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{guide.description}</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-[#7A2D2D]">
              <Play className="w-3 h-3" />
              <span>{guide.steps.length} étapes</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GuideDetail({ guide, onBack }) {
  const Icon = guide.icon;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowRight className="w-4 h-4 rotate-180" />
          Retour
        </Button>
      </div>
      
      {/* Title */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-[#7A2D2D]/10">
          <Icon className="w-8 h-8 text-[#7A2D2D]" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-heading font-bold text-secondary">{guide.title}</h2>
            {guide.isNew && (
              <Badge className="bg-green-100 text-green-700">Nouveau</Badge>
            )}
          </div>
          <p className="text-muted-foreground">{guide.description}</p>
        </div>
      </div>
      
      {/* Image */}
      {guide.image && (
        <Card className="overflow-hidden">
          <img 
            src={guide.image} 
            alt={guide.title}
            className="w-full h-auto max-h-80 object-cover"
          />
        </Card>
      )}
      
      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#7A2D2D]" />
            Étapes à suivre
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {guide.steps.map((step, index) => (
            <div key={index} className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#7A2D2D] text-white flex items-center justify-center font-bold text-sm">
                {step.step}
              </div>
              <div className="flex-1 pt-1">
                <h4 className="font-semibold text-secondary">{step.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default function HelpPage() {
  const { user, isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [activeTab, setActiveTab] = useState("guides");
  
  const userRole = user?.role || "membre";
  const isBureau = userRole === "bureau" || isAdmin();
  
  // Filtrer les guides selon la recherche
  const filteredGuides = useMemo(() => {
    const result = { membre: [], bureau: [], admin: [] };
    
    Object.entries(detailedGuides).forEach(([role, guides]) => {
      result[role] = guides.filter(guide => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        if (guide.title.toLowerCase().includes(query)) return true;
        if (guide.description.toLowerCase().includes(query)) return true;
        return guide.steps.some(
          step => step.title.toLowerCase().includes(query) || step.description.toLowerCase().includes(query)
        );
      });
    });
    
    return result;
  }, [searchQuery]);
  
  // URLs des guides PDF
  const guideUrls = {
    membre: `${API_URL}/api/docs/Guide_Membre_FoodTruckUnited.pdf`,
    bureau: `${API_URL}/api/docs/Guide_Bureau_FoodTruckUnited.pdf`,
    admin: `${API_URL}/api/docs/Guide_Admin_FoodTruckUnited.pdf`
  };
  
  if (selectedGuide) {
    return (
      <div className="space-y-6" data-testid="help-page">
        <GuideDetail guide={selectedGuide} onBack={() => setSelectedGuide(null)} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-[#7A2D2D]/10">
            <HelpCircle className="w-10 h-10 text-[#7A2D2D]" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-secondary">Centre d'aide</h1>
            <p className="text-muted-foreground">Guides détaillés, tutoriels et FAQ</p>
          </div>
        </div>
        
        {/* Barre de recherche */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher dans l'aide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {/* Nouveautés */}
      {recentUpdates.length > 0 && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Sparkles className="w-5 h-5" />
              Nouveautés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {recentUpdates.map((update, index) => (
                <div key={index} className="bg-white p-3 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-muted-foreground">{update.date}</span>
                    {update.isNew && (
                      <Badge className="bg-green-100 text-green-700 text-xs">Nouveau</Badge>
                    )}
                  </div>
                  <p className="font-medium text-secondary text-sm">{update.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{update.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="guides" className="gap-2">
            <BookOpen className="w-4 h-4" />
            Guides pas à pas
          </TabsTrigger>
          <TabsTrigger value="faq" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            Questions rapides
          </TabsTrigger>
        </TabsList>
        
        {/* Guides Tab */}
        <TabsContent value="guides">
          <Card>
            <CardHeader>
              <CardTitle>Tutoriels détaillés</CardTitle>
              <CardDescription>Cliquez sur un guide pour voir les étapes détaillées</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="membre" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="membre" className="gap-2">
                    <Users className="w-4 h-4" />
                    Membre
                  </TabsTrigger>
                  {isBureau && (
                    <TabsTrigger value="bureau" className="gap-2">
                      <Briefcase className="w-4 h-4" />
                      Bureau
                    </TabsTrigger>
                  )}
                  {isAdmin() && (
                    <TabsTrigger value="admin" className="gap-2">
                      <Shield className="w-4 h-4" />
                      Admin
                    </TabsTrigger>
                  )}
                </TabsList>
                
                <TabsContent value="membre">
                  <div className="grid gap-4 md:grid-cols-2">
                    {filteredGuides.membre.map(guide => (
                      <GuideCard key={guide.id} guide={guide} onSelect={setSelectedGuide} />
                    ))}
                  </div>
                  {filteredGuides.membre.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Aucun guide trouvé</p>
                  )}
                </TabsContent>
                
                {isBureau && (
                  <TabsContent value="bureau">
                    <div className="grid gap-4 md:grid-cols-2">
                      {filteredGuides.bureau.map(guide => (
                        <GuideCard key={guide.id} guide={guide} onSelect={setSelectedGuide} />
                      ))}
                    </div>
                    {filteredGuides.bureau.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">Aucun guide trouvé</p>
                    )}
                  </TabsContent>
                )}
                
                {isAdmin() && (
                  <TabsContent value="admin">
                    <div className="grid gap-4 md:grid-cols-2">
                      {filteredGuides.admin.map(guide => (
                        <GuideCard key={guide.id} guide={guide} onSelect={setSelectedGuide} />
                      ))}
                    </div>
                    {filteredGuides.admin.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">Aucun guide trouvé</p>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* FAQ Tab */}
        <TabsContent value="faq">
          <Card>
            <CardHeader>
              <CardTitle>Questions fréquentes</CardTitle>
              <CardDescription>Réponses rapides aux questions courantes</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="membre" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="membre">Membre</TabsTrigger>
                  {isBureau && <TabsTrigger value="bureau">Bureau</TabsTrigger>}
                  {isAdmin() && <TabsTrigger value="admin">Admin</TabsTrigger>}
                </TabsList>
                
                <TabsContent value="membre" className="space-y-3">
                  {quickFAQ.membre.map((item, index) => (
                    <div key={index} className="bg-[#F9F7F5] p-4 rounded-lg">
                      <p className="font-medium text-secondary mb-1">{item.q}</p>
                      <p className="text-sm text-muted-foreground">{item.a}</p>
                    </div>
                  ))}
                </TabsContent>
                
                {isBureau && (
                  <TabsContent value="bureau" className="space-y-3">
                    {quickFAQ.bureau.map((item, index) => (
                      <div key={index} className="bg-[#F9F7F5] p-4 rounded-lg">
                        <p className="font-medium text-secondary mb-1">{item.q}</p>
                        <p className="text-sm text-muted-foreground">{item.a}</p>
                      </div>
                    ))}
                  </TabsContent>
                )}
                
                {isAdmin() && (
                  <TabsContent value="admin" className="space-y-3">
                    {quickFAQ.admin.map((item, index) => (
                      <div key={index} className="bg-[#F9F7F5] p-4 rounded-lg">
                        <p className="font-medium text-secondary mb-1">{item.q}</p>
                        <p className="text-sm text-muted-foreground">{item.a}</p>
                      </div>
                    ))}
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Guides PDF */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#7A2D2D]" />
            Documentation PDF
          </CardTitle>
          <CardDescription>Téléchargez les guides complets</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="gap-2" asChild>
              <a href={guideUrls.membre} target="_blank" rel="noopener noreferrer">
                <FileText className="w-4 h-4" />
                Guide Membre
              </a>
            </Button>
            {isBureau && (
              <Button variant="outline" className="gap-2" asChild>
                <a href={guideUrls.bureau} target="_blank" rel="noopener noreferrer">
                  <FileText className="w-4 h-4" />
                  Guide Bureau
                </a>
              </Button>
            )}
            {isAdmin() && (
              <Button variant="outline" className="gap-2" asChild>
                <a href={guideUrls.admin} target="_blank" rel="noopener noreferrer">
                  <FileText className="w-4 h-4" />
                  Guide Admin
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Contact */}
      <Card className="bg-[#F9F7F5]">
        <CardContent className="pt-6">
          <div className="text-center">
            <MessageSquare className="w-12 h-12 text-[#7A2D2D] mx-auto mb-3" />
            <h3 className="font-heading font-semibold text-lg text-secondary mb-2">
              Besoin d'aide supplémentaire ?
            </h3>
            <p className="text-muted-foreground mb-4">
              Contactez-nous par email pour toute question
            </p>
            <Button variant="default" className="bg-[#7A2D2D] hover:bg-[#5C2121]" asChild>
              <a href="mailto:contact@foodtruckunited.fr">
                Contacter le support
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
